/**
 * モジュール設定 — global pause switches for whole app modules (admin only).
 * Pausing hides the module for everyone (launcher/nav/routes) and blocks
 * its backend endpoints. Fully reversible; no data is changed.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { PauseCircle, Storefront, Package, ClipboardText, ListChecks } from '@phosphor-icons/react';
import { Switch } from '@/components/ui/switch';
import { useAuth } from '@/auth/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODULE_ICONS = { marketing: Storefront, shizai: Package, parts_request: ClipboardText };

export default function ModuleSettings() {
  const { refreshMe } = useAuth();
  const [modules, setModules] = useState(null);
  const [navFlags, setNavFlags] = useState([]);
  const [saving, setSaving] = useState(null);

  const load = async () => {
    const res = await axios.get(`${API}/module-settings`);
    setModules(res.data.modules || []);
    setNavFlags(res.data.nav_flags || []);
  };

  const toggleFlag = async (flag, enabled) => {
    setSaving(flag.key);
    try {
      await axios.put(`${API}/module-settings/flags/${flag.key}`, { paused: enabled });
      await load();
      await refreshMe();
      toast.success(`${flag.label} を${enabled ? '有効' : '無効'}にしました`);
    } catch (_e) {
      toast.error('保存に失敗しました');
    } finally {
      setSaving(null);
    }
  };
  useEffect(() => { load().catch(() => toast.error('読み込みに失敗しました')); }, []);

  const toggle = async (mod, paused) => {
    setSaving(mod.key);
    try {
      await axios.put(`${API}/module-settings/${mod.key}`, { paused });
      await load();
      await refreshMe();
      toast.success(paused ? `${mod.label} を一時停止しました` : `${mod.label} を再開しました`);
    } catch (_e) {
      toast.error('保存に失敗しました');
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="max-w-2xl" data-testid="module-settings-page">
      <div className="flex items-center gap-3 mb-1">
        <span className="w-9 h-9 rounded-xl bg-teal-700 text-white flex items-center justify-center">
          <PauseCircle size={20} weight="fill" />
        </span>
        <h1 className="text-xl font-semibold text-zinc-900">モジュール設定</h1>
      </div>
      <p className="text-sm text-zinc-500 mb-6">
        モジュールを一時停止すると、全社員のランチャー・ナビから非表示になり、APIも遮断されます。データは削除されず、いつでも再開できます。
      </p>

      {/* シンプルメニュー（2026-06） — メニューを「やること」単位に絞る。
          OFF にすると従来のメニューがそのまま戻る。データは変わらない。 */}
      {(() => {
        const menu = navFlags.find((f) => f.key === 'simple_menu');
        if (!menu) return null;
        const mfgFlags = navFlags.filter((f) => f.kind === 'mfg');
        return (
          <section className="mb-8" data-testid="simple-menu-card">
            <div className="flex items-center gap-4 rounded-lg border border-teal-200 bg-teal-50/50 p-4">
              <span className="w-10 h-10 rounded-xl bg-teal-700 text-white flex items-center justify-center shrink-0">
                <ListChecks size={20} weight="fill" />
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-zinc-900">{menu.label}</p>
                <p className="text-xs text-zinc-600 mt-0.5">{menu.desc}</p>
                <p className="text-[11px] text-zinc-500 mt-1">
                  ON のとき: 場所・カテゴリ・品番は 設定 → マスター のみ／設備・貸出・フロアマップ・部品表は1画面／
                  AIの道具は ナレッジ（管理者のみ）。
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-xs text-zinc-500 whitespace-nowrap">{menu.enabled ? 'ON' : 'OFF'}</span>
                <Switch checked={menu.enabled} disabled={saving === menu.key}
                  onCheckedChange={(on) => toggleFlag(menu, on)}
                  data-testid="simple-menu-toggle" />
              </div>
            </div>
            {menu.enabled && mfgFlags.length > 0 && (
              <div className="mt-3 rounded-lg border border-zinc-200 bg-white p-3" data-testid="mfg-nav-flags">
                <p className="text-xs font-bold text-zinc-700">シンプルメニュー中に隠している製造の画面</p>
                <p className="text-[11px] text-zinc-500 mt-0.5">
                  必要になったら1画面ずつ戻せます（画面とデータは消えていません）。
                </p>
                <div className="mt-2 divide-y divide-zinc-100">
                  {mfgFlags.map((f) => (
                    <label key={f.key} className="flex items-center gap-3 py-2"
                      data-testid={`nav-flag-row-${f.key}`}>
                      <span className="flex-1 text-[13px] text-zinc-800">{f.label}</span>
                      <span className="text-[11px] text-zinc-400">{f.enabled ? '表示' : '非表示'}</span>
                      <Switch checked={f.enabled} disabled={saving === f.key}
                        onCheckedChange={(on) => toggleFlag(f, on)}
                        data-testid={`nav-flag-toggle-${f.key}`} />
                    </label>
                  ))}
                </div>
              </div>
            )}
          </section>
        );
      })()}

      {!modules && <p className="text-sm text-zinc-400">読み込み中…</p>}
      <div className="space-y-3">
        {modules?.map((mod) => {
          const Icon = MODULE_ICONS[mod.key] || PauseCircle;
          return (
            <div key={mod.key}
              data-testid={`module-row-${mod.key}`}
              className={`flex items-center gap-4 rounded-lg border p-4 transition-colors ${
                mod.paused ? 'border-amber-300 bg-amber-50/60' : 'border-zinc-200 bg-white'
              }`}>
              <span className={`w-10 h-10 rounded-xl text-white flex items-center justify-center shrink-0 ${
                mod.paused ? 'bg-zinc-400' : 'bg-teal-700'
              }`}>
                <Icon size={20} weight="fill" />
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-bold text-zinc-900">{mod.label}</p>
                  {mod.paused && (
                    <span className="text-[10px] font-bold text-amber-700 bg-amber-100 border border-amber-200 rounded-full px-2 py-0.5 whitespace-nowrap shrink-0"
                      data-testid={`module-paused-badge-${mod.key}`}>一時停止中</span>
                  )}
                </div>
                <p className="text-xs text-zinc-500 mt-0.5">{mod.desc}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-xs text-zinc-500 whitespace-nowrap">{mod.paused ? '停止' : '稼働中'}</span>
                <Switch
                  checked={!mod.paused}
                  disabled={saving === mod.key}
                  onCheckedChange={(on) => toggle(mod, !on)}
                  data-testid={`module-toggle-${mod.key}`}
                />
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-[11px] text-zinc-400 mt-4">
        ※ 停止中でも他モジュールからの内部データ連携（製造との同期など）は継続します。
      </p>
    </div>
  );
}
